<template>
    <div class="vueperslides__track-inner">
        <div class="vueperslide cursor-pointer" @click="sendEmit()">
            <div class="image-container">
                <img loading=lazy :src="joinPaths(currentImage)" alt="">
                <!-- Navigation arrows for multiple images -->
                <div v-if="hasMultipleImages" class="image-navigation">
                    <button 
                        class="nav-arrow nav-arrow-left" 
                        @click.stop="previousImage"
                        :disabled="currentImageIndex === 0"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <button 
                        class="nav-arrow nav-arrow-right" 
                        @click.stop="nextImage"
                        :disabled="currentImageIndex === experienceContent.images.length - 1"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                <!-- Image indicators -->
                <div v-if="hasMultipleImages" class="image-indicators">
                    <span 
                        v-for="(image, index) in experienceContent.images" 
                        :key="index"
                        class="indicator"
                        :class="{ active: index === currentImageIndex }"
                        @click.stop="goToImage(index)"
                    ></span>
                </div>
            </div>
            <div v-if="experienceContent.isFeatured" class="featured-badge">
                <span class="badge">{{ $t("type.featured") }}</span>
            </div>
            <div class="d-block vueperslide__content-wrapper position-relative">
                <div class="vueperslide__title">
                    <h3 class="secondary-color fw-bold" v-html="sanitizeHtml(processedName)"></h3>
                </div>
                <div class="vueperslide__content mt-3">
                    <p class="short-description" v-html="sanitizeHtml(experienceContent.shortDescription)"></p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { API_HELPER } from "@/helpers/api.js";
import { sanitizeHtml } from '@/helpers/sanitize.js';

export default {
    props: {
        experienceContent: {
            type: Object
        }
    },
    data() {
        return {
            currentImageIndex: 0
        }
    },
    methods: {
        joinPaths(relativePath){
            return API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, relativePath]); 
        },
        sendEmit(){
            this.$emit('click')
        },
        previousImage() {
            if (this.currentImageIndex > 0) {
                this.currentImageIndex--;
            }
        },
        nextImage() {
            if (this.currentImageIndex < this.experienceContent.images.length - 1) {
                this.currentImageIndex++;
            }
        },
        goToImage(index) {
            this.currentImageIndex = index;
        },
        sanitizeHtml
    },
    computed: {
        hasMultipleImages() {
            return this.experienceContent && this.experienceContent.images && this.experienceContent.images.length > 1;
        },
        currentImage() {
            if (this.experienceContent && this.experienceContent.images && this.experienceContent.images.length > 0) {
                return this.experienceContent.images[this.currentImageIndex] || '/default.jpg';
            }
            return '/default.jpg';
        },
        processedName(){
            const prefix = "Quinta Do Barbusano";
            const reg = "(\".*\")";

            var result = this.experienceContent ? this.experienceContent.name : '';
            result = result.replace(prefix, prefix + "<br>");
            result = result.replace(new RegExp(reg), "$1<br>");

            return result;
        }
    }
}
</script>

<style scoped>
.vueperslide {
    position: relative;
}

.image-container {
    position: relative;
    width: 100%;
    height: clamp(180px, 35vh, 280px);
    overflow: hidden;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.image-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, 
        rgba(0, 0, 0, 0.1) 0%, 
        transparent 20%, 
        transparent 80%, 
        rgba(0, 0, 0, 0.1) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: 1;
    pointer-events: none;
}

.vueperslide:hover .image-container::before {
    opacity: 1;
}

.vueperslide img{
    object-fit: cover;
    width: 100%;
    height: 100%;
    border-radius: 8px;
}

.vueperslide__content{
    color: black;
}

.vueperslide__title {
    margin-top: 32px;
    margin-bottom: 0.5rem;
}

.short-description {
    font-size: 14px;
    line-height: 1.4;
    color: #666;
    max-height: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.featured-badge {
    position: absolute;
    top: 15px;
    right: 15px;
    z-index: 2;
    pointer-events: none;
}

.badge {
    background-color: var(--primary-color);
    color: white;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

@media screen and (max-width: 767px) {
    .featured-badge {
        top: 12px;
        right: 8px;
    }
}

/* Image Navigation Styles */
.image-navigation {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    display: flex;
    justify-content: space-between;
    padding: 0 15px;
    pointer-events: none;
    z-index: 10;
}

.nav-arrow {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5));
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: auto;
    opacity: 0;
    transform: scale(0.7) translateY(-10px);
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.nav-arrow::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 0;
    height: 0;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transition: all 0.3s ease;
}

.nav-arrow:hover:not(:disabled)::before {
    width: 100%;
    height: 100%;
}

.nav-arrow:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.7));
    border-color: rgba(255, 255, 255, 0.4);
    transform: scale(1.1) translateY(0);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}

.nav-arrow:active:not(:disabled) {
    transform: scale(0.95) translateY(0);
}

.nav-arrow:disabled {
    opacity: 0.2;
    cursor: not-allowed;
    background: rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.1);
}

.vueperslide:hover .nav-arrow {
    opacity: 1;
    transform: scale(1) translateY(0);
}

.nav-arrow svg {
    width: 18px;
    height: 18px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
    transition: transform 0.2s ease;
}

.nav-arrow:hover:not(:disabled) svg {
    transform: scale(1.1);
}

/* Image Indicators */
.image-indicators {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 10;
}

.vueperslide:hover .image-indicators {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
}

.indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid rgba(255, 255, 255, 0.2);
    position: relative;
    backdrop-filter: blur(5px);
}

.indicator::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 0;
    height: 0;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transition: all 0.3s ease;
}

.indicator:hover::before {
    width: 100%;
    height: 100%;
}

.indicator.active {
    background: var(--primary-color);
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.3);
    box-shadow: 0 0 15px rgba(var(--primary-color-rgb, 0, 123, 255), 0.5);
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 15px rgba(var(--primary-color-rgb, 0, 123, 255), 0.5);
    }
    50% {
        box-shadow: 0 0 25px rgba(var(--primary-color-rgb, 0, 123, 255), 0.8);
    }
    100% {
        box-shadow: 0 0 15px rgba(var(--primary-color-rgb, 0, 123, 255), 0.5);
    }
}

.indicator.active::before {
    background: rgba(255, 255, 255, 0.3);
    width: 100%;
    height: 100%;
}

.indicator:hover:not(.active) {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
}

@media screen and (max-width: 767px) {
    .vueperslide img{
        height: 75%;
    }
    
    .nav-arrow {
        width: 30px;
        height: 30px;
        padding: 0 6px;
    }
    
    .nav-arrow svg {
        width: 15px;
        height: 15px;
    }
    
    .indicator {
        width: 8px;
        height: 8px;
        gap: 6px;
    }
    
    .image-indicators {
        gap: 6px;
        bottom: 12px;
    }
}

/* Adicional para espaçamento entre experiências (se for via classe experience-item do parent) */
:global(.experience-item) {
    margin: 0 32px !important;
}
</style>

